/**
 * SYMBOL-AUTHORITY (bloco I1 🔴) — extraído de marketdata.jsx na modularização
 * dos god-objects. Fonte única de: consolidação de família WIN/WDO
 * (FUT/FUL ↔ contrato ativo via campo `O` do backend), resolução de símbolo
 * (display e TRADING — o chokepoint gated 08:55), metadados (lote, tick,
 * horário de abertura) e ciclo de vida do set de símbolos (fetch, cache,
 * refresh com dedup/backoff, freshness ladder).
 *
 * Estado permanece NA INSTÂNCIA Marketdata (`md.symbols`, `md.symbolsTimestamp`,
 * `md.objectSymbolTable`, `md._symbolsRefreshInFlight`, `md._lastRefreshAttempt`)
 * e as chamadas internas passam por `md.<método>` (delegates da classe) —
 * stubs/espiões de instância em testes continuam funcionando e o comportamento
 * é idêntico ao pré-extração.
 *
 * Contratos pinados ANTES da extração:
 * - __tests__/symbolAuthorityContract.test.js (family/resolve/freshness)
 * - __tests__/orderConversionsGateSeam.integration.test.js (gate REAL no submit)
 * NÃO reimplementar resolução/expiração fora daqui (I1; issues #318/#351).
 */
import * as utilities from "./utilites";
import { settings, parsedMarketdataUrls } from "./settings";
import logger from "../../utils/logger";
import {
  getCachedSymbols,
  setCachedSymbols,
  isCacheFresh,
} from "../../utils/symbolsCache";
import { urlManager } from "../../utils/urlManager";

// Symbol Filter: futures master rows carry the active contract in `O`
const FILTER_FUTURES_MASTER = "H";
const SYMBOLS_COMMAND = "/api/marketdata/symbols";

/**
 * Check if symbol is a consolidatable family member
 * Only returns true for: FUT, FUL, or exact current contract match
 * @param {string} symbol - Symbol code
 * @returns {{family: string, canonical: string}|null} - Family info or null if not consolidatable
 */
export const getConsolidatableFamily = (md, symbol) => {
  if (!symbol) return null;

  // Check WIN family
  if (symbol === "WINFUT" || symbol === "WINFUL") {
    return { family: "WIN", canonical: "WINFUT" };
  }
  // Check WDO family
  if (symbol === "WDOFUT" || symbol === "WDOFUL") {
    return { family: "WDO", canonical: "WDOFUT" };
  }

  // Check if symbol is the exact current contract from objectSymbolTable
  const objTable = md.getObjectSymbolTable();

  // Check if this symbol is the current WIN contract
  if (objTable.get("WINFUT") === symbol) {
    return { family: "WIN", canonical: "WINFUT" };
  }
  // Check if this symbol is the current WDO contract
  if (objTable.get("WDOFUT") === symbol) {
    return { family: "WDO", canonical: "WDOFUT" };
  }

  // Not a consolidatable symbol (could be next contract, expired, or non-futures)
  return null;
};

/**
 * Get canonical symbol for SignalR subscription
 * Only consolidates FUT, FUL, and exact current contract
 * Other symbols (next contract, expired) get their own subscription
 * @param {string} symbol - Symbol code (e.g., "WING26", "WINFUL", "WINFUT", "WINI26")
 * @returns {string} - Canonical symbol (e.g., "WINFUT") or original symbol if not consolidatable
 */
export const getCanonicalSymbol = (md, symbol) => {
  const familyInfo = md.getConsolidatableFamily(symbol);
  return familyInfo ? familyInfo.canonical : symbol;
};

/**
 * Check if two symbols should share the same subscription
 * Only true if both are consolidatable to the same canonical symbol
 * @param {string} symbol1 - First symbol
 * @param {string} symbol2 - Second symbol
 * @returns {boolean} - True if both consolidate to the same canonical
 */
export const isSameFamily = (md, symbol1, symbol2) => {
  const family1 = md.getConsolidatableFamily(symbol1);
  const family2 = md.getConsolidatableFamily(symbol2);

  // Both must be consolidatable and share the same canonical
  return family1 && family2 && family1.canonical === family2.canonical;
};

/**
 * Get all related symbols for a family
 * @param {string} symbol - Symbol code
 * @returns {string[]} - Array of related symbols (FUT, FUL, and current contract)
 */
export const getRelatedSymbols = (md, symbol) => {
  const familyInfo = md.getConsolidatableFamily(symbol);
  if (!familyInfo) return [symbol];

  const related = new Set();
  const { family, canonical } = familyInfo;

  // Always include FUT and FUL variants
  related.add(canonical);
  related.add(family + "FUL");

  // Add current contract from objectSymbolTable (e.g., WING26, WDOG26)
  const objTable = md.getObjectSymbolTable();
  if (objTable.has(canonical)) {
    related.add(objTable.get(canonical));
  }

  return Array.from(related);
};

// Maps each futures master (WINFUT, WDOFUT, …) to the active contract the
// BACKEND already resolved in the `O` (ObjectSymbol) field. We TRUST `O` —
// no client-side expiration math. If `O` is ever wrong, the fix is in the
// backend (vendor `simbolos` table), not a date heuristic here.
export const getObjectSymbolTable = (md) => {
  if (md.objectSymbolTable.size === 0) {
    md.symbols.forEach((symbolData) => {
      if (
        symbolData &&
        symbolData.F === FILTER_FUTURES_MASTER &&
        symbolData.I &&
        symbolData.O
      ) {
        md.objectSymbolTable.set(symbolData.I, symbolData.O);
      }
    });
  }
  return md.objectSymbolTable;
};

// 08:55 America/Sao_Paulo of the current day, in epoch ms. Brazil has no DST
// since 2019, so the fixed -03:00 offset is correct; the DATE portion is read
// in SP tz so the daily boundary is right regardless of the browser timezone.
// 08:55 = feeder restart (~08:53) + margin, by which the backend has the day's
// rolled contracts published in `O`.
export const _gate0855Today = (md) => {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: "America/Sao_Paulo",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(new Date());
  const get = (t) => parts.find((p) => p.type === t).value;
  return new Date(
    `${get("year")}-${get("month")}-${get("day")}T08:55:00-03:00`,
  ).getTime();
};

// True only if the loaded symbols are this trading day's set: downloaded after
// today's 08:55 BRT rollover AND not older than 1h10. Fail-closed on unknown.
export const symbolsFreshForTrading = (md) => {
  if (!md.symbolsTimestamp) return false; // unknown → fail-closed
  if (md.symbolsTimestamp < md._gate0855Today()) return false; // pre-rollover
  if (Date.now() - md.symbolsTimestamp > 70 * 60 * 1000) return false; // > 1h10
  return true;
};

// Fire a background symbols refresh, deduped (one in flight) and backed off
// (max 1x/60s). Returns true if a refresh was dispatched.
export const _dispatchSymbolsRefresh = (md) => {
  const now = Date.now();
  if (md._symbolsRefreshInFlight) return false;
  if (md._lastRefreshAttempt && now - md._lastRefreshAttempt < 60 * 1000)
    return false;
  md._symbolsRefreshInFlight = true;
  md._lastRefreshAttempt = now;
  md.refreshSymbolsInBackground().finally(() => {
    md._symbolsRefreshInFlight = false;
  });
  return true;
};

// Trade-time gate: returns whether symbols are fresh enough to translate a
// FUT/FUL order, and fires a refresh in the background when they are not.
export const ensureSymbolsFreshForTrading = (md) => {
  if (md.symbolsFreshForTrading()) return true;
  md._dispatchSymbolsRefresh();
  return false;
};

// Heartbeat-driven self-heal: renews symbols hourly (10min ahead of the trade
// gate's 1h10) and right after crossing today's 08:55 BRT rollover. Called
// from the "HB" handler only (never "Q" — hot path). Cheap no-op when fresh.
export const _maybeRefreshSymbolsOnHeartbeat = (md) => {
  if (!md.symbolsTimestamp) {
    md._dispatchSymbolsRefresh();
    return;
  }
  const now = Date.now();
  const gate = md._gate0855Today();
  if (now >= gate && md.symbolsTimestamp < gate) {
    md._dispatchSymbolsRefresh(); // crossed the daily rollover boundary
    return;
  }
  if (now - md.symbolsTimestamp > 60 * 60 * 1000) {
    md._dispatchSymbolsRefresh(); // hourly refresh
  }
};

/**
 * Resolve an input symbol to the active contract — pure display/quote path.
 * Trusts the backend `O` field (via `getObjectSymbolTable`). Never throws;
 * does NOT gate on freshness. For ORDER submission use
 * `resolveSymbolForTrading`, the single point that also enforces the 08:55
 * freshness gate.
 *
 * @param {string} symbol - 'WINFUT', 'WINFUL', 'WINM26', 'PETR4', etc.
 * @returns {string} active series for FUT/FUL aliases, or `symbol` as-is
 */
export const resolveSymbol = (md, symbol) => {
  if (!symbol) return symbol;
  if (symbol.endsWith("FUT") || symbol.endsWith("FUL")) {
    const futSymbol = symbol.endsWith("FUL")
      ? symbol.slice(0, -3) + "FUT"
      : symbol;
    const mapped = md.getObjectSymbolTable().get(futSymbol);
    if (mapped) return mapped;
  }
  return symbol;
};

/**
 * THE single resolution authority for ORDER submission. Every order path must
 * translate FUT/FUL through this method — never reimplement resolution or
 * expiration logic elsewhere (that was issue #318's two-place duplication).
 *
 * Enforces the 08:55 BRT freshness gate before trusting `O`, then resolves.
 * Throws (blocking the order) if symbols aren't this day's set, or if the
 * alias couldn't be translated. Fires a background refresh on a stale gate so
 * the next attempt can pass.
 *
 * @param {string} symbol - order symbol (FUT/FUL alias OR an already-resolved
 *   futures series like WINM26 — both are gated)
 * @returns {string} resolved contract for FUT/FUL aliases, or `symbol` as-is
 */
export const resolveSymbolForTrading = (md, symbol) => {
  if (!symbol) return symbol;
  const isAlias = symbol.endsWith("FUT") || symbol.endsWith("FUL");
  // Gate ONLY continuous-series aliases (FUT/FUL). They are the only symbols
  // that need resolution and can resolve to the WRONG contract before the daily
  // 08:55 BRT rollover — which is exactly what this gate protects (#318/#351).
  // An explicit series (e.g. WINM26, F === "F") carries no resolution ambiguity,
  // so it is NOT gated. The alias is intact here because order ENTRY translation
  // funnels through this single gated authority (createOrder /
  // convertOrderObjectToRouterOrder / boleta), never the non-gated display
  // resolver `resolveSymbol` (which stays for display/position/cancel).
  if (!isAlias) return symbol;
  if (!md.ensureSymbolsFreshForTrading()) {
    throw new Error(
      "Estamos atualizando os contratos futuros. Aguarde alguns segundos e tente novamente — ou envie pela série atual do contrato.",
    );
  }
  const resolved = md.resolveSymbol(symbol);
  if (resolved.endsWith("FUT") || resolved.endsWith("FUL")) {
    throw new Error(
      "Ainda carregando os contratos futuros. Aguarde alguns segundos e tente novamente — ou envie pela série atual do contrato.",
    );
  }
  return resolved;
};

export const getSymbol = (md, code) => {
  if (md.symbols.has(code)) {
    return md.symbols.get(code);
  }
  return null;
};

/**
 * FOQ (fator de cotação) — multiplicador que converte pontos em R$.
 *
 * FONTE ÚNICA: a tabela de símbolos do servidor. O cliente NUNCA fabrica
 * multiplicador — havia 3 cópias de uma tabela fixa (WIN=0.2, WDO=10…) e 2
 * sites usando 1, dando R$ divergente na mesma tela quando a tabela faltava.
 *
 * - símbolo AUSENTE da tabela → `null` (sem dado → quem exibe esconde o R$,
 *   nunca chuta dinheiro);
 * - registro presente sem campo FOQ → `1` (convenção do backend para à vista;
 *   ver chartDataProcessor: "stocks have FOQ=1 or undefined").
 */
export const getFOQ = (md, code) => {
  const si = md.symbols.get(code);
  if (!si) return null;
  return typeof si.FOQ === "number" ? si.FOQ : 1;
};

export const getStandardLot = (md, code) => {
  if (md.symbols.has(code)) {
    return md.symbols.get(code).L;
  }
  return 1;
};

export const getMinInc = (md, code) => {
  if (md.symbols.has(code)) {
    return md.symbols.get(code).MPT;
  }
  return 0.01;
};

/**
 * Get market opening hour for a symbol based on its exchange
 * @param {string} symbol - Symbol code (e.g., "WINFUT", "PETR4")
 * @returns {number} - Market opening hour (9 for BMF, 10 for BOVESPA/default)
 */
export const getMarketOpenHour = (md, symbol) => {
  // Get symbol metadata to determine exchange
  const symbolData = md.getSymbol(symbol);
  const exchange = symbolData?.E; // "BMF", "BOVESPA", etc.

  // Use runtime-config marketHours if available
  if (exchange && settings.marketHours?.[exchange] !== undefined) {
    return settings.marketHours[exchange];
  }

  // Fallback to default market open hour
  return settings.marketHours?.default || 10;
};

export const getSymbols = async (md, cb, options = {}) => {
  const { useCache = true, backgroundRefresh = false } = options;
  // Set when we loaded a cache that's stale (TTL or pre-08:55-rollover): the
  // freshly fetched data below must REPLACE the in-memory set, not just refill
  // the cache, so `symbolsTimestamp` reflects this day's symbols.
  let staleCacheNeedsReplace = false;

  // Try cache first if enabled
  if (useCache && md.symbols.size === 0) {
    try {
      const cached = await getCachedSymbols();

      if (cached && cached.data) {
        console.log(
          `[${new Date().toISOString()}] 📦 Loading ${cached.data.length} symbols from cache`,
        );

        const m = new Map();
        cached.data.forEach((item) => {
          if (item && item.I) {
            m.set(item.I, item);
          }
        });
        md.symbols = m;
        md.symbolsTimestamp = cached.timestamp || 0;
        md.objectSymbolTable.clear(); // Force recalculation of futures series mapping

        // Cache is good enough to use as-is only if it's within the TTL AND we
        // haven't crossed today's 08:55 BRT rollover with a pre-rollover cache.
        // The latter forces a fresh download right after the daily contract
        // roll so FUT/FUL `O` reflects today's series.
        const gate = md._gate0855Today();
        const crossedRolloverStale =
          Date.now() >= gate && md.symbolsTimestamp < gate;
        const isFresh = (await isCacheFresh()) && !crossedRolloverStale;

        if (isFresh) {
          console.log(
            `[${new Date().toISOString()}] ✅ Cache is fresh, symbols loaded instantly`,
          );

          if (cb) cb(cached.data);

          // Schedule background refresh if requested
          if (backgroundRefresh) {
            console.log(
              `[${new Date().toISOString()}] 🔄 Scheduling background symbols refresh...`,
            );
            setTimeout(() => {
              md.refreshSymbolsInBackground();
            }, 5000); // Wait 5 seconds before background refresh
          }

          return cached.data;
        } else {
          console.log(
            `[${new Date().toISOString()}] ⚠️  Cache is stale, fetching fresh data...`,
          );
          staleCacheNeedsReplace = true;
        }
      }
    } catch (error) {
      console.error("[Marketdata] Error reading symbols cache:", error);
      // Continue to fetch from API
    }
  }

  // Fetch from API (either no cache, stale cache, or cache disabled)
  const serviceUrl = `${md.getUrl()}${SYMBOLS_COMMAND}`;
  try {
    console.log(
      `[${new Date().toISOString()}] 🌐 Fetching symbols from API...`,
    );
    const response = await utilities.getStringFromURL(
      serviceUrl,
      md.signalRToken,
    );
    const ret = JSON.parse(response);

    // Guard: never replace a healthy symbols set (or cache) with a suspiciously small
    // response — the backend returns [] when MySQL is down or the table is empty.
    const MIN_SYMBOLS = 1000;
    const isValidResponse = Array.isArray(ret) && ret.length >= MIN_SYMBOLS;

    if (!isValidResponse) {
      console.warn(
        `[${new Date().toISOString()}] ⚠️ Symbols API returned ${Array.isArray(ret) ? ret.length : "non-array"} symbols — skipping update (possible server-side error)`,
      );
      if (md.symbols.size === 0) {
        throw new Error(
          `Symbols API returned ${Array.isArray(ret) ? ret.length : "invalid data"} symbols`,
        );
      }
    } else {
      if (md.symbols.size === 0 || !useCache || staleCacheNeedsReplace) {
        const m = new Map();
        ret.forEach((item) => {
          if (item && item.I) {
            m.set(item.I, item);
          }
        });
        md.symbols = m;
        md.symbolsTimestamp = Date.now();
        md.objectSymbolTable.clear(); // Force recalculation of futures series mapping
      }

      // Cache the fresh data
      if (useCache) {
        setCachedSymbols(ret).catch((err) => {
          console.error("[Marketdata] Failed to cache symbols:", err);
        });
      }
    }

    // Mark server as healthy after successful symbols fetch
    const urls = parsedMarketdataUrls[md.broker] || [];
    if (urls.length > 1) {
      urlManager.markSuccess(md.broker, "marketdata");
    }

    if (cb) cb(ret);
    return ret;
  } catch (error) {
    logger.data.error("Falha ao carregar símbolos", {
      error: error.message,
      broker: md.broker,
      server: md.getUrl(),
    });

    // Mark server as unhealthy - this allows failover in startUsingAuth retry loop
    const urls = parsedMarketdataUrls[md.broker] || [];
    if (urls.length > 1) {
      urlManager.markFailureAndGetNext(md.broker, "marketdata", urls, error);
    }

    utilities.errorHandler(error);
    throw error; // Re-throw to trigger failover in startUsingAuth
  }
};

/**
 * Refresh symbols in the background without blocking
 */
export const refreshSymbolsInBackground = async (md) => {
  try {
    console.log(
      `[${new Date().toISOString()}] 🔄 Background refresh: Fetching latest symbols...`,
    );
    const serviceUrl = `${md.getUrl()}${SYMBOLS_COMMAND}`;
    const response = await utilities.getStringFromURL(
      serviceUrl,
      md.signalRToken,
    );
    const ret = JSON.parse(response);

    if (!Array.isArray(ret) || ret.length < 1000) {
      console.warn(
        `[${new Date().toISOString()}] ⚠️ Background refresh: API returned ${Array.isArray(ret) ? ret.length : "non-array"} symbols — skipping update`,
      );
      return;
    }

    // Update in-memory symbols
    const m = new Map();
    ret.forEach((item) => {
      if (item && item.I) {
        m.set(item.I, item);
      }
    });
    md.symbols = m;
    md.symbolsTimestamp = Date.now();
    md.objectSymbolTable.clear(); // Force recalculation of futures series mapping

    // Update cache
    await setCachedSymbols(ret);
    console.log(
      `[${new Date().toISOString()}] ✅ Background refresh completed: ${ret.length} symbols updated`,
    );
  } catch (error) {
    console.error("[Marketdata] Background symbols refresh failed:", error);
    // Don't show user error - this is a background operation
  }
};
